import * as Sentry from "./errorReporting";
import { authedApiFetch } from "./api";
import { APP_VERSION } from "./constants";

// delete-bet-client (#1214 PR B, spec §16.4).
// Single job: call POST /api/delete-bet for one bet and classify the result.
// Reads: the bet id. Writes: nothing (the route owns the delete; the caller owns client state).
// Does NOT: touch bets state, sign the user out on a 401 (no global 401 handler exists, and the
// route maps a GoTrue 429 to 401), or treat an arbitrary 404 as removed (an HTML 404 from a
// missing route or the native fallback base is a failure -- P1-3).
//
// Result: { removed: true } | { removed: false, kind: "auth"|"rate"|"network"|"server"|"update", message }
//       | { removed: false, kind: "state_changed", settled } (#1731 B1).
// #1731 B1 (spec R1.4, R2.5, R2.7): every call sends x-sliptrack-app-version and the settled state the user was shown
// (`expectSettled`, a boolean; anything else is refused before the request). Once B2 gates the route: 409
// {status:"state_changed", settled} means the bet's settled state changed since the sheet opened -- nothing was deleted,
// the caller re-renders and waits for a new tap (no automatic retry); 426 {error:"update_required"} on a build that sends
// the header is a config bug (captureException). Before B2 the route reads only betId and never answers 409/426.
// Sentry levels (P2-3, CC-IMPL-037): 400 / 403 / malformed 2xx-404 / unknown status ->
// captureException (a client or config bug, nothing retries it); 500 / 502 -> breadcrumb only
// (the route already captured it); 503 / network throw -> captureMessage warning (transient).

export const REMOVE_ERROR = {
  auth: "Sign in again to remove bets.",
  rate: "Too many removals right now. Try again later.",
  network: "Couldn't remove this bet. Check your connection and try again.",
  server: "Couldn't remove this bet. Try again in a moment.",
  update: "Update SlipTrack to remove bets.",
};

const REMOVED_200 = new Set(["deleted", "already_deleted"]);
const TAGS = { function: "delete-bet-client" };

function fail(kind) {
  return { removed: false, kind, message: REMOVE_ERROR[kind] };
}

function exception(message, extra) {
  Sentry.captureException(new Error(message), { tags: TAGS, extra });
}

// -> the parsed JSON body, or undefined when the response is not JSON (or fails to parse).
async function readJsonBody(res) {
  const contentType = (res.headers && res.headers.get && res.headers.get("content-type")) || "";
  if (!contentType.includes("application/json")) return undefined;
  try {
    return await res.json();
  } catch (e) {
    // A JSON content-type with an unparseable body is a malformed response; the caller
    // classifies it (captureException) -- recorded here too so the parse error is not lost.
    Sentry.captureException(e, { tags: { ...TAGS, stage: "parse" }, extra: { status: res.status } });
    return undefined;
  }
}

function bodyStatus(body) {
  return body && typeof body === "object" && typeof body.status === "string" ? body.status : null;
}

export async function deleteBet(betId, { expectSettled } = {}) {
  if (betId === null || betId === undefined || betId === "") {
    exception("delete-bet-client: missing bet id", { betId: String(betId) });
    return fail("server");
  }
  if (typeof expectSettled !== "boolean") {
    exception("delete-bet-client: invalid expectSettled", { type: typeof expectSettled });
    return fail("server");
  }

  let res;
  try {
    res = await authedApiFetch("/api/delete-bet", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-sliptrack-app-version": APP_VERSION },
      body: JSON.stringify({ betId: String(betId), expectSettled }),
    });
  } catch (e) {
    // Transient: the user can tap Remove again (CC-IMPL-037).
    Sentry.captureMessage("delete-bet-client: request failed", { level: "warning", tags: { ...TAGS, stage: "request" }, extra: { error: e && e.message } });
    return fail("network");
  }

  const status = res.status;
  if (status === 200 || status === 404) {
    const body = await readJsonBody(res);
    const s = bodyStatus(body);
    if (status === 200 && s && REMOVED_200.has(s)) return { removed: true };
    if (status === 404 && s === "not_found") return { removed: true };
    exception(`delete-bet-client: unexpected ${status} response`, { status, bodyStatus: s });
    return fail("network");
  }
  if (status === 409) {
    const body = await readJsonBody(res);
    // Expected race (B2+): the bet settled (or un-settled) while the sheet was open. No Sentry event.
    if (bodyStatus(body) === "state_changed" && typeof body.settled === "boolean") {
      // The server answers state_changed only on a mismatch, so one that reports the state just sent is a route/wiring
      // bug: report it rather than let every tap loop on the same notice (pre-Codex FC-1, CC-OBS-005).
      if (body.settled === expectSettled) {
        exception("delete-bet-client: state_changed without a change", { status });
        return fail("server");
      }
      return { removed: false, kind: "state_changed", settled: body.settled };
    }
    exception("delete-bet-client: unexpected 409 response", { status, bodyStatus: bodyStatus(body) });
    return fail("server");
  }
  if (status === 426) {
    const body = await readJsonBody(res);
    if (body && typeof body === "object" && body.error === "update_required") {
      // This build sends the header, so a refusal means the route's minimum is ahead of a shipped build.
      exception("delete-bet-client: update_required on a current build", { status, appVersion: APP_VERSION });
      return fail("update");
    }
    exception("delete-bet-client: unexpected 426 response", { status });
    return fail("server");
  }
  if (status === 401) {
    Sentry.addBreadcrumb({ category: "delete-bet", level: "info", message: "delete-bet 401" });
    return fail("auth");
  }
  if (status === 429) {
    Sentry.addBreadcrumb({ category: "delete-bet", level: "info", message: "delete-bet 429" });
    return fail("rate");
  }
  if (status === 403) {
    exception("delete-bet-client: 403 origin refused", { status });
    return fail("network");
  }
  if (status === 500 || status === 502) {
    Sentry.addBreadcrumb({ category: "delete-bet", level: "error", message: `delete-bet ${status}` });
    return fail("server");
  }
  if (status === 503) {
    Sentry.captureMessage("delete-bet-client: service unavailable", { level: "warning", tags: { ...TAGS, stage: "http" }, extra: { status } });
    return fail("server");
  }
  exception(`delete-bet-client: unexpected status ${status}`, { status });
  return fail("server");
}
